import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { SiteHeader6 } from "@/components/layout/SiteHeader6";
import { SiteFooter6 } from "@/components/layout/SiteFooter6";
import { InteriorHero } from "@/components/common/InteriorHero";
import { Box6 } from "@/components/common/Box6";
import { Cta6 } from "@/components/common/Cta6";
import { TextLink6 } from "@/components/common/TextLink6";
import { Pill6 } from "@/components/common/Pill6";
import { FocusAreas6 } from "@/components/sections6/FocusAreas6";
import { resourceCategories6, pillars6, pillarSlugToIndex6, brand6 } from "@/brand/contentF6";
import { fetchResource } from "@/lib/resourcesApi";
import { usePageMeta } from "@/lib/usePageMeta";

const catLabel = Object.fromEntries(resourceCategories6.map((c) => [c.id, c.label]));

/**
 * ResourceDetailPage — one library item, in this order:
 * TL;DR → full content → related service pillar → Book a consultation.
 */
export default function ResourceDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setItem(null);
    setError(false);
    fetchResource(slug)
      .then((r) => active && setItem(r))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [slug]);

  const relatedPillar =
    item?.related_pillar && pillarSlugToIndex6[item.related_pillar] !== undefined
      ? pillars6[pillarSlugToIndex6[item.related_pillar]]
      : null;

  usePageMeta({
    title: error ? "Resource not found" : item?.title || "Resources",
    description: item?.summary,
    type: item ? "article" : "website",
    noindex: error,
  });

  if (error) {
    return (
      <main data-testid="resource-not-found" style={{ background: C.surfaceLight }}>
        <SiteHeader6 />
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-32 text-center">
          <p className="font-cormorant" style={{ color: C.textOnLight, fontSize: "1.8rem" }}>
            This resource is no longer available.
          </p>
          <div className="mt-6 flex justify-center">
            <TextLink6 to="/resources">Back to Resources</TextLink6>
          </div>
        </div>
        <SiteFooter6 />
      </main>
    );
  }

  if (!item) {
    return (
      <main data-testid="resource-loading" style={{ background: C.surfaceLight, minHeight: "100vh" }}>
        <SiteHeader6 />
      </main>
    );
  }

  const paragraphs = (item.body || "").split(/\n\n+/).filter(Boolean);
  const label = catLabel[item.category] || item.category;

  return (
    <main data-testid="resource-detail-page" style={{ background: C.surfaceLight, color: C.textOnLight }}>
      <SiteHeader6 />

      <InteriorHero
        breadcrumb={[{ label: "Faith 1st Consulting", href: "/" }, { label: "Resources", href: "/resources" }, { label }]}
        eyebrow={label}
        title={item.title}
        meta={[
          ["Author", item.author],
          ["Published", item.publish_date],
          ["Category", label],
          ...(relatedPillar ? [["Related service", relatedPillar.label]] : []),
        ]}
      />

      <article data-testid="resource-body" style={{ background: C.surfaceLight2 }}>
        <div className="mx-auto max-w-[920px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20 space-y-8">
          {/* 1 — TL;DR */}
          <Box6 tone="white" radius="resource" className="p-8 lg:p-9" data-testid="resource-tldr">
            <div className="flex items-center gap-3 mb-4">
              <Pill6 tone="blue">TL;DR</Pill6>
              <span className="font-inter text-[10px] uppercase font-semibold" style={{ color: C.inkOnLightSoft, letterSpacing: "0.22em" }}>
                The short version
              </span>
            </div>
            <p className="font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.2rem, 1.6vw, 1.45rem)", lineHeight: 1.35 }}>
              {item.summary}
            </p>
          </Box6>

          {/* 2 — Full content */}
          <Box6 tone="white" radius="resource" className="p-8 lg:p-11" data-testid="resource-content">
            {item.featured_image && (
              <img
                src={item.featured_image}
                alt={item.title}
                data-testid="resource-featured-image"
                className="w-full mb-10 block"
                style={{ border: `1px solid ${C.brassLine}`, borderRadius: 8 }}
              />
            )}
            <div className="space-y-6">
              {paragraphs.map((p, i) => (
                <p key={i} className="font-inter leading-[1.8]" style={{ color: C.inkOnLight, fontSize: "1.05rem" }}>
                  {p}
                </p>
              ))}
            </div>
            {item.download_url && (
              <div className="mt-10">
                <Cta6 href={item.download_url} external testId="resource-download-cta" arrow={false}>
                  <DownloadSimple size={15} weight="bold" />
                  {item.download_label || "Download"}
                </Cta6>
              </div>
            )}
          </Box6>

          {/* 3 — Related service pillar */}
          {relatedPillar && (
            <Box6 tone="parchment" radius="resource" className="p-8 lg:p-9" data-testid="resource-related-pillar">
              <p className="font-inter text-[10px] uppercase font-semibold mb-4" style={{ color: C.accentMetal, letterSpacing: "0.26em" }}>
                Related service · {relatedPillar.n} of 04
              </p>
              <div className="grid md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-5">
                  <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "1.5rem", lineHeight: 1.15, fontWeight: 400 }}>
                    {relatedPillar.label}
                  </h3>
                  <p className="mt-3 font-inter leading-[1.65]" style={{ color: C.inkOnLight, fontSize: "0.95rem" }}>
                    {relatedPillar.outcome}
                  </p>
                  <TextLink6 to={`/services/${relatedPillar.slug}`} className="mt-5" testId="resource-related-pillar-link">
                    Explore {relatedPillar.label}
                  </TextLink6>
                </div>
                <div className="md:col-span-7">
                  <FocusAreas6 items={relatedPillar.focusAreas} />
                </div>
              </div>
            </Box6>
          )}

          {/* 4 — CTA */}
          <Box6 tone="blue" radius="card" className="p-8 lg:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="resource-cta">
            <div>
              <p className="font-inter text-[10px] uppercase font-semibold mb-3" style={{ color: C.accentMetal, letterSpacing: "0.26em" }}>
                Carrying something similar?
              </p>
              <p className="font-cormorant" style={{ color: C.textOnDark, fontSize: "1.5rem", lineHeight: 1.15 }}>
                Every inquiry is confidential and reviewed personally.
              </p>
            </div>
            <Cta6 to="/contact" variant="light" testId="resource-cta-btn" className="px-7 py-4 shrink-0">
              {brand6.ctaLabel}
            </Cta6>
          </Box6>

          <div className="pt-2">
            <TextLink6 to="/resources" testId="back-to-resources">Back to Resources</TextLink6>
          </div>
        </div>
      </article>

      <SiteFooter6 />
    </main>
  );
}
